import exifr from 'exifr';

export interface ExifValidationResult {
  latitude?: number;
  longitude?: number;
  dateTime?: string;
  device?: string;
  raw?: Record<string, unknown>;
  warnings: ExifWarning[];
}

export interface ExifWarning {
  code: 'NO_GPS' | 'NO_DATETIME' | 'DATETIME_FUTURE' | 'DATETIME_OLD' | 'GPS_MISMATCH';
  message: string;
  severity: 'info' | 'warn' | 'error';
}

/**
 * Parse EXIF data from an image file using the exifr library.
 * Validates GPS coordinates, timestamp, and device info.
 * Generates warnings for inconsistencies.
 */
export async function parseAndValidateExif(
  file: File,
  options?: {
    expectedLatitude?: number;
    expectedLongitude?: number;
    maxDistanceKm?: number; // default 50km
    maxAgeDays?: number; // default 7 days
  }
): Promise<ExifValidationResult> {
  const warnings: ExifWarning[] = [];

  try {
    // Parse EXIF with exifr - supports JPEG, TIFF, HEIC, AVIF, PNG
    const parsed = await exifr.parse(file, {
      gps: true,
      // Get all tags including Make, Model, DateTimeOriginal, etc.
      pick: [
        'GPSLatitude', 'GPSLongitude', 'GPSLatitudeRef', 'GPSLongitudeRef',
        'DateTimeOriginal', 'CreateDate', 'ModifyDate',
        'Make', 'Model', 'Software',
        'ImageWidth', 'ImageHeight', 'Orientation',
      ],
    });

    if (!parsed) {
      warnings.push({
        code: 'NO_GPS',
        message: 'Sem dados EXIF na foto. A imagem pode ter sido editada ou transferida.',
        severity: 'warn',
      });
      return { warnings };
    }

    const result: ExifValidationResult = {
      raw: parsed,
      warnings,
    };

    // --- GPS Validation ---
    const lat = parsed.latitude ?? parsed.GPSLatitude;
    const lng = parsed.longitude ?? parsed.GPSLongitude;

    if (lat !== undefined && lng !== undefined) {
      result.latitude = lat;
      result.longitude = lng;

      // Validate against expected location if provided
      if (options?.expectedLatitude !== undefined && options?.expectedLongitude !== undefined) {
        const distance = haversineKm(lat, lng, options.expectedLatitude, options.expectedLongitude);
        const maxDist = options.maxDistanceKm ?? 50;
        if (distance > maxDist) {
          warnings.push({
            code: 'GPS_MISMATCH',
            message: `Localização GPS da foto está ${distance.toFixed(1)}km do local esperado (máx: ${maxDist}km).`,
            severity: 'error',
          });
        }
      }
    } else {
      warnings.push({
        code: 'NO_GPS',
        message: 'Foto sem dados de geolocalização. Ative o GPS da câmera.',
        severity: 'warn',
      });
    }

    // --- DateTime Validation ---
    const dateStr = parsed.DateTimeOriginal ?? parsed.CreateDate ?? parsed.ModifyDate;
    if (dateStr) {
      const photoDate = dateStr instanceof Date ? dateStr : new Date(dateStr);
      if (!isNaN(photoDate.getTime())) {
        result.dateTime = photoDate.toISOString();

        const now = new Date();
        if (photoDate > new Date(now.getTime() + 24 * 60 * 60 * 1000)) {
          warnings.push({
            code: 'DATETIME_FUTURE',
            message: 'Data da foto é no futuro. Verifique o relógio do dispositivo.',
            severity: 'error',
          });
        }

        const maxAge = options?.maxAgeDays ?? 7;
        const ageMs = now.getTime() - photoDate.getTime();
        const ageDays = ageMs / (1000 * 60 * 60 * 24);
        if (ageDays > maxAge) {
          warnings.push({
            code: 'DATETIME_OLD',
            message: `Foto tirada há ${Math.round(ageDays)} dias (máx: ${maxAge} dias).`,
            severity: 'warn',
          });
        }
      }
    } else {
      warnings.push({
        code: 'NO_DATETIME',
        message: 'Foto sem data/hora original. Metadados podem ter sido removidos.',
        severity: 'info',
      });
    }

    // --- Device info ---
    const make = parsed.Make || '';
    const model = parsed.Model || '';
    if (make || model) {
      result.device = `${make} ${model}`.trim();
    }

    return result;
  } catch (err) {
    console.warn('EXIF parsing error:', err);
    warnings.push({
      code: 'NO_GPS',
      message: 'Não foi possível ler metadados EXIF da foto.',
      severity: 'info',
    });
    return { warnings };
  }
}

/**
 * Parse EXIF from multiple files in batch. Returns results keyed by index.
 */
export async function parseBatchExif(
  files: File[],
  options?: Parameters<typeof parseAndValidateExif>[1]
): Promise<ExifValidationResult[]> {
  return Promise.all(files.map(f => parseAndValidateExif(f, options)));
}

/**
 * Haversine distance between two lat/lng points in km.
 */
function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}
